import { Address, getAddress, zeroAddress } from 'viem'
import { Entities, Entity } from '../data/entities'

export enum GovernorType {
  UNKNOWN = 'UNKNOWN',
  KNOWN = 'KNOWN',
  UNGOVERNED = 'UNGOVERNED',
}

export interface GovernorInfo {
  address?: Address
  key?: string
  logo?: string
  name?: string
  type: GovernorType
}

export interface VaultGovernors {
  governors: GovernorInfo[]
  hasGovernorAddressMismatch?: boolean
}

const createGovernorInfo = (
  key: string,
  name: string,
  logo: string,
  address: Address,
  type: GovernorType = GovernorType.KNOWN,
): GovernorInfo => ({
  key,
  name,
  logo,
  address: getAddress(address),
  type,
})

const createUnknownGovernorInfo = (address: Address): GovernorInfo => ({
  address: getAddress(address),
  type: GovernorType.UNKNOWN,
})

// Cache a reverse index (lowercased address -> [(key, entity)]) per entities instance
// so repeated lookups across many vaults are O(1) instead of O(n*m).
const entityIndexCache = new WeakMap<Entities, Map<string, Array<[string, Entity]>>>()

function getEntityIndex(entities: Entities): Map<string, Array<[string, Entity]>> {
  const cached = entityIndexCache.get(entities)
  if (cached) return cached

  const index = new Map<string, Array<[string, Entity]>>()
  for (const [key, entity] of entities.entries()) {
    for (const addr of Object.keys(entity.addresses ?? {})) {
      const lowerAddr = addr.toLowerCase()
      const bucket = index.get(lowerAddr)
      if (bucket) {
        bucket.push([key, entity])
      } else {
        index.set(lowerAddr, [[key, entity]])
      }
    }
  }
  entityIndexCache.set(entities, index)
  return index
}

const findMatchingGovernors = (
  entities: Entities,
  governorAddress: Address,
) => {
  const index = getEntityIndex(entities)
  return index.get(governorAddress.toLowerCase()) ?? []
}

const processGovernorAddresses = (
  governorAddresses: readonly Address[],
  entities: Entities | undefined,
): GovernorInfo[] => {
  const allGovernors: GovernorInfo[] = []

  if (!entities) {
    return governorAddresses.map((address) =>
      createUnknownGovernorInfo(address),
    )
  }

  for (const governorAddress of governorAddresses) {
    const matchingGovernors = findMatchingGovernors(entities, governorAddress)

    if (matchingGovernors.length > 0) {
      allGovernors.push(
        ...matchingGovernors.map(([key, { name, logo }]) =>
          createGovernorInfo(key, name, logo, governorAddress),
        ),
      )
    } else {
      allGovernors.push(createUnknownGovernorInfo(governorAddress))
    }
  }

  return allGovernors
}

export function selectGovernorsFromAddresses(
  addresses: Address | Address[] | undefined,
  entities: Entities | undefined,
): VaultGovernors {
  if (!addresses) {
    return {
      governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
    }
  }

  let governorAddresses: readonly Address[] = []

  if (Array.isArray(addresses)) {
    governorAddresses = addresses
      .filter((addr): addr is Address => addr && addr !== zeroAddress)
      .map(getAddress)
  } else if (addresses && addresses !== zeroAddress) {
    governorAddresses = [getAddress(addresses)]
  }

  if (!governorAddresses.length) {
    return {
      governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
    }
  }

  if (!entities) {
    return {
      governors: governorAddresses.map((address) =>
        createUnknownGovernorInfo(address),
      ),
    }
  }

  const allGovernors = processGovernorAddresses(governorAddresses, entities)

  return {
    governors: allGovernors,
  }
}

